import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import {
  FormFieldContext,
  useFormField,
  type FormFieldContextValue,
} from "../../lib/form-field-context.js";
import {
  cardBoxClass,
  HORIZONTAL_FIELD_CLASS,
  HORIZONTAL_ROOT_CLASS,
  type FormFieldHintPlacement,
  type FormFieldLayout,
} from "../../lib/field-layout.js";
import { FormFieldLabelRow } from "./FormFieldLabelRow.js";

export { useFormField };
export type { FormFieldContextValue };

/* ---------------- FormField (C8 #4) ----------------
   The sanctioned form wrapper (strategy §15, §24, §25). It owns the label,
   help text, error message, and the required-field marker, and wires them to
   the control so product code cannot forget the a11y contract (§9 line 696):
   programmatic label association + `aria-describedby` (hint/error) +
   `aria-invalid` + `aria-required`.

   Two integration paths, both supported at once:
   • cloneElement — the single child control is cloned with id / aria-* /
     disabled injected. Works with no control changes for controls that spread
     their rest props to the focusable element (Input, Textarea).
   • FormFieldContext — curated controls (Select, Combobox, RadioGroup, …) read
     `useFormField()` and apply the wiring appropriate to their own structure
     (e.g. a radiogroup labels via `aria-labelledby`, not `htmlFor`). Those
     integrations land in the §25 form-standards follow-up.

   `as="fieldset"` is the group mode: a <fieldset> named by a <legend> in the
   label style, holding any number of controls (checkboxes, a value and its
   unit). The children are NOT cloned and the context is reset to null, so no
   child takes the group's id; the hint and error describe the fieldset.

   Form standards encoded here (strategy §25):
   • Required convention — we mark REQUIRED fields (red “*” + sr-only
     “(required)”), never optional ones. One convention, applied everywhere.
   • Help-text placement — hint sits below the control; the error message
     replaces it when invalid (consistent with the atom controls).
   • Error anatomy — the error region is `role="alert"` so it is announced;
     authors are asked (docs) to write what’s wrong + how to fix it. */

interface FormFieldBaseProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** When set (and not `false`), the field is invalid and this message shows. */
  error?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** `stacked` (default): today's markup. `horizontal`: the label in a
   *  gutter column from `sm` up, stacked below it. `card`: one bordered box
   *  around the label and the control — pair it with a ghost control.
   *  `lib/field-layout.ts` owns the classes; the card box's padding is part
   *  of the option, so FormField itself takes no padding class. */
  layout?: FormFieldLayout;
}

/** One control (the default): the child is cloned and the context provided. */
export interface FormFieldControlProps
  extends
    FormFieldBaseProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** The root element. `div` (default) wraps exactly one control. */
  as?: "div";
  /** Override the control id (otherwise taken from the child or auto-generated). */
  htmlFor?: string;
  /** `below` (default): the hint sits under the control, replaced by the
   *  error when invalid. `beside`: the hint trails the label instead, in
   *  the label row, and stays visible when an error shows below. */
  hintPlacement?: FormFieldHintPlacement;
  /** Trails the label row, OUTSIDE the `<label>` element so its own control
   *  never joins the field's accessible name. */
  actionsSlot?: React.ReactNode;
  /** Exactly one control element. */
  children: React.ReactElement;
}

/** Several controls under one `<fieldset>`, named by its `<legend>`. */
export interface FormFieldGroupProps
  extends
    FormFieldBaseProps,
    Omit<
      React.FieldsetHTMLAttributes<HTMLFieldSetElement>,
      "children" | "disabled"
    > {
  /** The root element. `fieldset` groups several controls under a legend. */
  as: "fieldset";
  /** A group has no single control for a label to point at. */
  htmlFor?: never;
  /** A group's legend has no beside hint or actions slot (single-control
   *  only — see FormFieldControlProps). */
  hintPlacement?: never;
  actionsSlot?: never;
  /** Any controls. None is cloned, and none reads the group's context. */
  children: React.ReactNode;
}

export type FormFieldProps = FormFieldControlProps | FormFieldGroupProps;

/* A browser fieldset has a border, padding, margin and a min-content minimum
   width. Resetting them lines a group up with the stacked fields around it. */
const FIELDSET_RESET = "m-0 min-w-0 border-0 p-0";

const joinIds = (...ids: (string | undefined)[]) =>
  ids.filter(Boolean).join(" ") || undefined;

export const FormField = forwardRef<
  HTMLDivElement | HTMLFieldSetElement,
  FormFieldProps
>(function FormField(
  {
    as,
    label,
    hint,
    error,
    required = false,
    disabled = false,
    layout = "stacked",
    hintPlacement = "below",
    actionsSlot,
    htmlFor,
    children,
    className,
    ...rest
  },
  ref,
) {
  const auto = React.useId();
  const hintId = hint != null ? `${auto}-hint` : undefined;
  const errorId = error != null ? `${auto}-error` : undefined;
  const invalid = error != null && error !== false;
  const besideHint = hintPlacement === "beside";

  const labelRow = (tag: "label" | "legend", id?: string, forId?: string) => (
    <FormFieldLabelRow
      tag={tag}
      id={id}
      htmlFor={forId}
      required={required}
      label={label}
      besideHint={besideHint}
      hint={hint}
      hintId={hintId}
      actionsSlot={actionsSlot}
    />
  );
  const message = invalid ? (
    <p id={errorId} role="alert" className="text-dense-1 text-pb-red-text mt-1">
      {error}
    </p>
  ) : !besideHint && hint != null ? (
    <p id={hintId} className="text-dense-1 text-pb-text-muted mt-1">
      {hint}
    </p>
  ) : null;

  // Horizontal is single-control only (its gutter is two columns, not a
  // third for a group's own children); a fieldset keeps its base look under
  // it. Card applies to both — cardBoxClass replaces FIELDSET_RESET's
  // border-0/p-0 via cn()'s last-wins merge.
  const layoutClass =
    layout === "card"
      ? cardBoxClass(invalid)
      : layout === "horizontal" && as !== "fieldset"
        ? HORIZONTAL_ROOT_CLASS
        : undefined;

  if (as === "fieldset") {
    const { "aria-describedby": ownDescribedBy, ...groupRest } =
      rest as React.FieldsetHTMLAttributes<HTMLFieldSetElement>;
    return (
      <FormFieldContext.Provider value={null}>
        <fieldset
          ref={ref as React.Ref<HTMLFieldSetElement>}
          className={cn("w-full", FIELDSET_RESET, layoutClass, className)}
          disabled={disabled || undefined}
          aria-describedby={joinIds(
            ownDescribedBy,
            hintId,
            invalid ? errorId : undefined,
          )}
          {...groupRest}
        >
          {labelRow("legend")}
          {children}
          {message}
        </fieldset>
      </FormFieldContext.Provider>
    );
  }

  const child = React.Children.only(children) as React.ReactElement<
    Record<string, unknown>
  >;
  const childProps = child.props;

  const id =
    htmlFor ?? (childProps.id as string | undefined) ?? `${auto}-control`;
  const labelId = label != null ? `${auto}-label` : undefined;
  const describedBy = joinIds(
    childProps["aria-describedby"] as string | undefined,
    hintId,
    invalid ? errorId : undefined,
  );

  const ctx: FormFieldContextValue = {
    id,
    labelId,
    describedBy,
    invalid,
    required,
    disabled,
  };

  const cloned = React.cloneElement(child, {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": invalid
      ? true
      : (childProps["aria-invalid"] as boolean | undefined),
    "aria-required": required || childProps["aria-required"] ? true : undefined,
    disabled: disabled || (childProps.disabled as boolean | undefined),
  });

  // Horizontal's field wrapper puts the control and the message in the
  // grid's second column together; every other layout renders them as
  // FormField's own direct children, exactly as before.
  const field =
    layout === "horizontal" ? (
      <div className={HORIZONTAL_FIELD_CLASS}>
        {cloned}
        {message}
      </div>
    ) : (
      <>
        {cloned}
        {message}
      </>
    );

  return (
    <FormFieldContext.Provider value={ctx}>
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn("w-full", layoutClass, className)}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      >
        {labelRow("label", labelId, id)}
        {field}
      </div>
    </FormFieldContext.Provider>
  );
});
